import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ShoppingBag, Download, FileText, Baby, Layers, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { api, API, img, money, fmtSize, errMsg, fmtDate } from "../lib/api";
import { useCart, useAuth } from "../lib/store";
import { Stars, ProductCard, Empty } from "../components/Bits";
import NewsletterBox from "../components/NewsletterBox";

import { useSeo, crumbs } from "../components/Seo";

function Gallery({ images, name }) {
  const [i, setI] = useState(0);
  const list = images.length ? images : [""];
  return (
    <div>
      <div className="rounded-[32px] overflow-hidden bg-[var(--blush)] aspect-square border border-[#f6e3ec]">
        <img src={img(list[i])} alt={name} className="w-full h-full object-contain" data-testid="product-main-image" />
      </div>
      {list.length > 1 && (
        <div className="flex gap-3 mt-4 overflow-x-auto no-scrollbar">
          {list.map((p, k) => (
            <button key={k} onClick={() => setI(k)} data-testid={`product-thumb-${k}`} className={`shrink-0 w-20 h-20 rounded-2xl overflow-hidden border-2 transition-colors ${k === i ? "border-[var(--pink)]" : "border-transparent"}`}>
              <img src={img(p)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function FreeFiles({ p }) {
  const [token, setToken] = useState(localStorage.getItem("mps_free"));
  if (!token) return <NewsletterBox compact title="Stáhněte si zdarma" text="Stačí odebírat náš newsletter a materiál je váš." onUnlocked={setToken} />;
  if (!p.files.length) return <p className="font-hand text-lg text-[var(--muted)]">Soubory se právě připravují, brzy budou ke stažení.</p>;
  return (
    <div className="space-y-2" data-testid="free-files">
      {p.files.map((f) => (
        <a key={f.id} href={`${API}/free/download/${p.id}/${f.id}?token=${token}`} data-testid={`free-download-${f.id}`} className="flex items-center justify-between gap-3 rounded-2xl bg-[var(--teal-soft)] px-5 py-4 font-semibold hover:-translate-y-0.5 transition-transform">
          <span className="flex items-center gap-2"><FileText className="w-4 h-4" /> {f.name}</span>
          <span className="flex items-center gap-2 text-sm">{fmtSize(f.size)} <Download className="w-4 h-4" /></span>
        </a>
      ))}
    </div>
  );
}

function Reviews({ pid }) {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], can_review: false });
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const load = () => api.get(`/products/${pid}/reviews`).then((r) => setData(r.data));
  useEffect(() => {
    load();
  }, [pid, user]); // eslint-disable-line
  const send = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/products/${pid}/reviews`, { rating, text });
      toast.success("Děkujeme za hodnocení!");
      setText("");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <section className="mt-20" data-testid="reviews-section">
      <h2 className="text-3xl font-semibold mb-6"><span className="squiggle">Hodnocení</span></h2>
      {data.can_review ? (
        <form onSubmit={send} className="bg-[var(--blush)] rounded-[28px] p-6 mb-8 space-y-3" data-testid="review-form">
          <p className="font-display text-lg">Jak se vám materiál líbil?</p>
          <Stars value={rating} size={28} onPick={setRating} />
          <textarea data-testid="review-text-input" value={text} onChange={(e) => setText(e.target.value)} rows={3} className="field" placeholder="Napište pár slov pro ostatní paní učitelky a rodiče…" />
          <button className="btn-pink" data-testid="review-submit-btn">Odeslat hodnocení</button>
        </form>
      ) : (
        <p className="text-sm text-[var(--muted)] mb-6">Hodnotit mohou zákazníci, kteří materiál zakoupili.{!user && <> <Link to="/prihlaseni" className="underline">Přihlásit se</Link></>}</p>
      )}
      {data.items.length === 0 ? <Empty>Zatím bez hodnocení – buďte první!</Empty> : (
        <div className="grid md:grid-cols-2 gap-4">
          {data.items.map((r) => (
            <div key={r.id} className="rounded-[24px] border border-[#f6e3ec] p-5" data-testid="review-item">
              <div className="flex justify-between items-center"><span className="font-bold">{r.name}</span><Stars value={r.rating} /></div>
              {r.text && <p className="mt-2 text-[#4a4752]">{r.text}</p>}
              <p className="text-xs text-[var(--muted)] mt-2">{fmtDate(r.created_at)}</p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const { addProduct } = useCart();
  useSeo({
    title: p ? p.seo_title || p.name : undefined,
    description: p ? p.seo_description || ((p.description || "").startsWith(p.short_desc || "\u0000") ? p.description : `${p.short_desc || ""} ${p.description || ""}`) : undefined,
    image: p?.images?.[0],
    type: "product",
    jsonld: p && ((s, abs) => [
      {
        "@context": "https://schema.org", "@type": "Product", name: p.name, sku: p.id, description: (p.description || p.short_desc || "").slice(0, 4000),
        image: p.images.map((x) => abs(s, x)), brand: { "@type": "Brand", name: s.company?.name || "Materiály pro školky" }, category: p.categories[0]?.name,
        offers: { "@type": "Offer", url: `${s.site_url}/produkt/${p.slug}`, priceCurrency: "CZK", price: Number(p.sale_price || p.price || 0).toFixed(2), availability: "https://schema.org/InStock", itemCondition: "https://schema.org/NewCondition" },
        ...(p.rating_count > 0 ? { aggregateRating: { "@type": "AggregateRating", ratingValue: p.rating_avg, reviewCount: p.rating_count } } : {}),
      },
      crumbs(s, [["E-shop", "/eshop"], ...(p.categories[0] ? [[p.categories[0].name, `/kategorie/${p.categories[0].slug}`]] : []), [p.name, `/produkt/${p.slug}`]]),
    ]),
  });
  useEffect(() => {
    setP(null);
    api.get(`/products/${slug}`).then((r) => setP(r.data)).catch(() => setErr(true));
  }, [slug]);
  if (err) return <Empty>Materiál jsme nenašli.</Empty>;
  if (!p) return <div className="h-[70vh]" />;
  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="product-detail">
      <Link to="/eshop" className="inline-flex items-center gap-1 text-sm font-bold text-[var(--muted)] hover:text-[var(--pink)] mb-6"><ChevronLeft className="w-4 h-4" /> Zpět do e-shopu</Link>
      <div className="grid lg:grid-cols-2 gap-12">
        <Gallery images={p.images} name={p.name} />
        <div>
          <div className="flex flex-wrap gap-2 mb-4">{p.categories.map((c) => <Link key={c.id} to={`/kategorie/${c.slug}`} className="text-xs font-bold px-3 py-1 rounded-full bg-[var(--pink-soft)] text-[var(--pink-dark)]">{c.name}</Link>)}</div>
          <h1 className="text-4xl sm:text-5xl font-semibold leading-tight" data-testid="product-name">{p.name}</h1>
          {p.rating_count > 0 && <div className="flex items-center gap-2 mt-3"><Stars value={p.rating_avg} size={18} /><span className="text-sm text-[var(--muted)]">{p.rating_avg} ({p.rating_count})</span></div>}
          <p className="mt-4 text-lg text-[var(--muted)]">{p.short_desc}</p>
          <div className="flex flex-wrap gap-4 mt-6 text-sm">
            {p.age && <span className="flex items-center gap-2"><Baby className="w-4 h-4 text-[var(--teal)]" /> {p.age}</span>}
            {p.format && <span className="flex items-center gap-2"><FileText className="w-4 h-4 text-[var(--teal)]" /> {p.format}</span>}
            {p.pages && <span className="flex items-center gap-2"><Layers className="w-4 h-4 text-[var(--teal)]" /> {p.pages} stran</span>}
          </div>
          <div className="mt-8 p-6 rounded-[28px] bg-[var(--blush)]">
            {p.is_free ? <FreeFiles p={p} /> : (
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  {p.sale_price && <span className="line-through text-[var(--muted)] mr-2">{money(p.price)}</span>}
                  <span className="text-3xl font-extrabold" data-testid="product-price">{money(p.sale_price || p.price)}</span>
                  <p className="text-xs text-[var(--muted)] mt-1">{p.physical ? "Tištěná publikace · doprava Zásilkovnou nebo Balíkovnou od 100 Kč" : "Digitální produkt · stažení po zaplacení"}</p>
                </div>
                <button className="btn-pink" onClick={() => addProduct(p)} data-testid="product-add-to-cart-btn"><ShoppingBag className="w-4 h-4" /> Do košíku</button>
              </div>
            )}
          </div>
          <div className="mt-8 whitespace-pre-line leading-relaxed text-[#4a4752]" data-testid="product-description">{p.description}</div>
        </div>
      </div>
      <Reviews pid={p.id} />
      {p.related.length > 0 && (
        <section className="mt-20">
          <h2 className="text-3xl font-semibold mb-8"><span className="squiggle">Mohlo by se vám líbit</span></h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">{p.related.map((r, i) => <ProductCard key={r.id} p={r} i={i} />)}</div>
        </section>
      )}
    </div>
  );
}
